import { MenuItem, Select, SelectChangeEvent, Stack, TextField, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import NumberFormatCustom from "../../trade/NumberField";
import TargetOrderBuyTokenModal from "src/components/trade/target-orders/target-modals/TargetOrderBuyTokens";
import { useDispatch } from "react-redux";
import {
  setErrorMessage,
  setFrequency,
  setSellFiatAmount,
  setSpendLimit,
  setSpendLimitError,
  useDcaSlice,
} from "src/store/features/dca.slice";
import { ReactNode } from "react";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import SellFiatCurrencyModal from "../SellFiatCurrencyModal";
import { DcaStrategyReqDto } from "src/store/features/api/generated";
import { StyledErrorMessage } from "src/components/application/styled-components/DropZone";
import { trackEvent } from "src/Amplitude";

export const InputWrapper = styled("div")(
  ({ selected, error }: { selected?: boolean; error?: boolean }) => {
    const baseStyledObject = {
      display: "flex",
      width: "100%",
      justifyContent: "space-between",
      padding: "12px",
      alignItems: "center",
      gap: "8px",
      borderRadius: "8px",
      border: "1px solid #E5E7E8",
      cursor: "pointer",
      "&:hover": {
        border: "1px solid #002035",
      },
      "&:focus-within": {
        border: "1px solid #002035",
      },
    };

    if (selected) baseStyledObject["border"] = "1px solid #002035";
    if (error) baseStyledObject["border"] = "1px solid #fc4c4c";
    return baseStyledObject;
  },
);

export const SelectWrapper = styled(Select)`
  width: 100%;
  border-radius: 8px;
  height: 43px;
`;

const StyledTextField = styled(TextField)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontFamily: "Söhne",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: "400",
  lineHeight: "17px",
  minWidth: "79px",
  alignSelf: "center",
}));

export const TokenNameDisplay = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontFamily: "Söhne",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: "400",
  alignContent: "center",
}));

export const ToggleContainer = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
});

export const StyledBoldTitle = styled(Typography)({
  color: "#002035",
  textAlign: "center",
  fontFamily: "Roobert",
  fontSize: "16px",
  fontStyle: "normal",
  fontWeight: "600",
  lineHeight: "19px",
  letterSpacing: "-0.32px",
});

export const SummaryLabel = styled(Typography)({
  color: "#002035",
  fontFamily: "Söhne",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: "400",
  lineHeight: "17px",
});

export const SmallLabel = styled("p")({
  color: "var(--Neutral-600, #5C6469)",
  fontFamily: "Söhne",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: "400",
  lineHeight: "17px",
  minWidth: "fit-content",
});

export const StyledDisclaimerText = styled(Typography)({
  color: "var(--Neutral-500, #7B858B)",
  fontFamily: "Söhne",
  fontSize: "12px",
  fontStyle: "normal",
  fontWeight: "400",
  lineHeight: "14px",
});

export const StyledLabel = ({ title }: { title: string }) => {
  return <SmallLabel>{title}</SmallLabel>;
};

export const CryptoSelection = () => {
  return (
    <InputWrapper>
      <TargetOrderBuyTokenModal source={"DCA"} />
    </InputWrapper>
  );
};

export function FrequencySelectorField() {
  const { frequency } = useDcaSlice();
  const dispatch = useDispatch();

  const frequencyOptions: DcaStrategyReqDto["frequency"][] = ["Day", "Week", "Month", "Quarter"];

  const handleChange = (event: SelectChangeEvent<unknown>, child: ReactNode) => {
    dispatch(setFrequency(event.target.value as DcaStrategyReqDto["frequency"]));
    trackEvent("Changed frequency", { frequency: event.target.value }, "DCA");
  };

  return (
    <SelectWrapper size="small" value={frequency} onChange={handleChange}>
      {frequencyOptions.map((item, it) => (
        <MenuItem key={item + it} value={item}>
          <Typography sx={{ fontSize: "14px", marginTop: "4px" }}>{item}</Typography>
        </MenuItem>
      ))}
    </SelectWrapper>
  );
}

export function StartDayPicker() {
  return (
    <SelectWrapper>
      <MenuItem>
        <DesktopDatePicker />
      </MenuItem>
    </SelectWrapper>
  );
}

export const SpendLimitField = () => {
  const dispatch = useDispatch();
  const { spendLimit, spendLimitError } = useDcaSlice();

  return (
    <>
      <InputWrapper style={{ padding: "8.5px" }} error={!!spendLimitError}>
        <StyledTextField
          variant="standard"
          defaultValue={spendLimit}
          placeholder={"Enter Spend Limit"}
          onChange={(e) => {
            dispatch(setSpendLimit(+e.target.value));
            dispatch(setSpendLimitError(""));
            trackEvent("Change spend limit", { value: +e.target.value }, "DCA");
          }}
          InputProps={{
            inputComponent: NumberFormatCustom as any,
            disableUnderline: true,
            sx: {
              ".MuiInput-input": { padding: "0px", width: "100%" },
            },
            inputProps: {
              maxLength: 11, // 10 digits and 1 decimal
            },
          }}
          sx={{ width: "100%" }}
        />
      </InputWrapper>
      {spendLimitError && <StyledErrorMessage>{spendLimitError}</StyledErrorMessage>}
    </>
  );
};

export const Chip = styled("div")(({ selected, theme }: { theme?: any; selected: boolean }) => {
  let defaultUnselectedStyle = {
    display: "flex",
    padding: "2px 8px",
    justifyContent: "center",
    borderRadius: "8px",
    border: "1px solid #E5E7E8",
    background: theme.palette.background.default,
    fontSize: "14px",
    cursor: "pointer",
    "&:hover": {
      background: theme.palette.primary.contrastText,
      border: `1px solid ${theme.palette.text.primary}`,
    },
  };

  if (selected) {
    defaultUnselectedStyle["border"] = `1px solid ${theme.palette.text.primary}`;
    defaultUnselectedStyle["background"] = theme.palette.primary.contrastText;
  }

  return defaultUnselectedStyle;
});

export default function SellAmountInputComponent({
  selected,
  error,
}: {
  selected?: boolean;
  error?: boolean;
}) {
  const dispatch = useDispatch();
  const { amount } = useDcaSlice();

  return (
    <InputWrapper selected={selected} style={{ padding: "8.5px", minWidth: "200px" }} error={error}>
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        flex="auto"
        justifyContent={"space-between"}>
        <StyledTextField
          variant="standard"
          placeholder={"0.00"}
          value={amount ?? ""}
          onFocus={() => dispatch(setErrorMessage(""))}
          onChange={(e) => {
            dispatch(setSellFiatAmount(+e.target.value));
          }}
          InputProps={{
            inputComponent: NumberFormatCustom as any,
            disableUnderline: true,
            sx: { ".MuiInput-input": { padding: "0px" } }, // Remove extra padding
            inputProps: {
              maxLength: 11, // 10 digits and 1 decimal
            },
          }}
        />
        <SellFiatCurrencyModal />
      </Stack>
    </InputWrapper>
  );
}
